import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { todayISO } from "../utils/format";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";

const EMPTY = {
  internal_number: "", brand: "", model: "", version: "", year: "", category: "SUV", fuel_type: "Diesel",
  transmission: "Automatique", color: "", mileage: "", seats: "", power: "", vin: "", engine_number: "",
  plate: "", dossier_number: "", purchase_date: todayISO(), origin_country: "", departure_port: "", arrival_port: "",
  status: "Commandé", purchase_price: "", purchase_currency: "FCFA", exchange_rate: "",
  target_sale_price: "", insurance_expiry: "", inspection_expiry: "",
  budget_total: "", budget_transport: "", budget_douane: "", budget_reparation: "", notes: "",
};

const Field = ({ label, k, type = "text", testid, form, set }) => (
  <div>
    <Label>{label}</Label>
    <Input type={type} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={testid || `vehicle-form-${k}`} />
  </div>
);

const Sel = ({ label, k, options, form, set }) => (
  <div>
    <Label>{label}</Label>
    <Select value={form[k] || undefined} onValueChange={(v) => set(k, v)}>
      <SelectTrigger data-testid={`vehicle-form-${k}`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
      <SelectContent>
        {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export default function VehicleFormDialog({ vehicle, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    api.get("/meta").then((r) => {
      setMeta(r.data);
      if (vehicle) {
        setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, vehicle[k] ?? EMPTY[k]])) });
      } else {
        setForm((f) => ({ ...EMPTY, exchange_rate: "" }));
      }
    });
  }, [open, vehicle]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.brand || !form.model) return toast.error("Marque et modèle requis");
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: form.year ? Number(form.year) : null,
        mileage: form.mileage ? Number(form.mileage) : null,
        seats: form.seats ? Number(form.seats) : null,
        insurance_expiry: form.insurance_expiry || null,
        inspection_expiry: form.inspection_expiry || null,
        purchase_price: form.purchase_price ? Number(form.purchase_price) : 0,
        exchange_rate: form.exchange_rate ? Number(form.exchange_rate) : null,
        target_sale_price: form.target_sale_price ? Number(form.target_sale_price) : null,
        budget_total: form.budget_total ? Number(form.budget_total) : null,
        budget_transport: form.budget_transport ? Number(form.budget_transport) : null,
        budget_douane: form.budget_douane ? Number(form.budget_douane) : null,
        budget_reparation: form.budget_reparation ? Number(form.budget_reparation) : null,
      };
      if (vehicle) {
        await api.patch(`/vehicles/${vehicle.id}`, payload);
        toast.success("Véhicule mis à jour");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Véhicule créé");
      }
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="add-vehicle-button"><Plus className="mr-1 h-4 w-4" /> Nouveau véhicule</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{vehicle ? `Modifier — ${vehicle.brand} ${vehicle.model}` : "Nouveau véhicule"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Field form={form} set={set} label="N° interne (auto si vide)" k="internal_number" />
          <Field form={form} set={set} label="Marque *" k="brand" />
          <Field form={form} set={set} label="Modèle *" k="model" />
          <Field form={form} set={set} label="Version" k="version" />
          <Field form={form} set={set} label="Année" k="year" type="number" />
          <Sel form={form} set={set} label="Catégorie" k="category" options={meta?.vehicle_categories} />
          <Sel form={form} set={set} label="Carburant" k="fuel_type" options={meta?.fuel_types} />
          <Sel form={form} set={set} label="Boîte de vitesse" k="transmission" options={meta?.transmissions} />
          <Field form={form} set={set} label="Couleur" k="color" />
          <Field form={form} set={set} label="Kilométrage" k="mileage" type="number" />
          <Field form={form} set={set} label="Places" k="seats" type="number" />
          <Field form={form} set={set} label="Puissance" k="power" />
          <Field form={form} set={set} label="N° châssis / VIN" k="vin" />
          <Field form={form} set={set} label="N° moteur" k="engine_number" />
          <Field form={form} set={set} label="Immatriculation" k="plate" />
          <Field form={form} set={set} label="N° du dossier" k="dossier_number" />
          <Field form={form} set={set} label="Expiration assurance" k="insurance_expiry" type="date" />
          <Field form={form} set={set} label="Expiration visite technique" k="inspection_expiry" type="date" />
          <Field form={form} set={set} label="Date d'achat" k="purchase_date" type="date" />
          <Field form={form} set={set} label="Pays d'origine" k="origin_country" />
          <Field form={form} set={set} label="Port de départ" k="departure_port" />
          <Field form={form} set={set} label="Port d'arrivée" k="arrival_port" />
          <Sel form={form} set={set} label="Statut" k="status" options={meta?.vehicle_statuses} />
          <Field form={form} set={set} label="Prix d'achat" k="purchase_price" type="number" />
          <Sel form={form} set={set} label="Devise d'achat" k="purchase_currency" options={meta?.currencies} />
          {form.purchase_currency !== "FCFA" && (
            <Field form={form} set={set} label={`Taux de change (1 ${form.purchase_currency} = X FCFA, défaut ${meta?.rates?.[form.purchase_currency] || ""})`} k="exchange_rate" type="number" />
          )}
          <Field form={form} set={set} label="Prix de vente cible (FCFA)" k="target_sale_price" type="number" />
          <Field form={form} set={set} label="Budget total prévu (FCFA)" k="budget_total" type="number" />
          <Field form={form} set={set} label="Budget transport" k="budget_transport" type="number" />
          <Field form={form} set={set} label="Budget douane" k="budget_douane" type="number" />
          <Field form={form} set={set} label="Budget réparation" k="budget_reparation" type="number" />
          <div className="col-span-2 md:col-span-3">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="vehicle-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="vehicle-form-submit">
            {saving ? "Enregistrement…" : vehicle ? "Mettre à jour" : "Créer le véhicule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
